import { ReactNode } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, ChevronRight, AlertTriangle, TrendingUp, BookOpen } from 'lucide-react';
import { MarketingHeader } from './MarketingHeader';
import { MarketingFooter } from './MarketingFooter';
import { SeoHead } from './SeoHead';

export interface SeoLandingSection {
  type: 'hook' | 'explain' | 'example' | 'solution' | 'cta' | 'faq' | 'links' | 'checklist' | 'midcta' | 'cluster';
  title?: string;
  content?: string;
  items?: string[];
  faq?: { q: string; a: string }[];
  links?: { label: string; href: string; internal?: boolean }[];
}

export interface SeoLandingPageData {
  seo: {
    title: string;
    description: string;
    canonical: string;
    keywords?: string;
  };
  hero: {
    tag?: string;
    title: string;
    subtitle: string;
    ctaLabel?: string;
    ctaHref?: string;
  };
  sections: SeoLandingSection[];
  pilierLink?: { label: string; href: string };
  relatedLinks?: { label: string; href: string }[];
  conversionLink?: { label: string; href: string };
}

interface Props {
  data: SeoLandingPageData;
  onNavigate?: (p: string) => void;
  onSignIn?: () => void;
  onSignUp?: () => void;
  children?: ReactNode;
}

export function SeoLandingLayout({ data, onNavigate, onSignIn, onSignUp, children }: Props) {
  const navigate = useNavigate();
  const nav = onNavigate ?? ((p: string) => { window.scrollTo(0, 0); navigate(p); });
  const signUp = onSignUp ?? (() => navigate('/login'));

  function renderSection(section: SeoLandingSection, idx: number) {
    switch (section.type) {
      case 'hook':
        return (
          <section key={idx} className="py-14 px-4 bg-amber-50 border-y border-amber-100">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <AlertTriangle size={20} className="text-white" />
                </div>
                <div>
                  {section.title && <h2 className="text-xl font-bold text-gray-900 mb-3">{section.title}</h2>}
                  {section.content && <p className="text-gray-700 leading-relaxed text-base">{section.content}</p>}
                  {section.items && (
                    <ul className="mt-4 space-y-2">
                      {section.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-700 text-sm">
                          <span className="text-amber-500 font-bold mt-0.5">→</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </section>
        );

      case 'explain':
        return (
          <section key={idx} className="py-16 px-4 bg-white">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-7 bg-blue-600 rounded-full" />
                <span className="text-sm font-semibold text-blue-600 uppercase tracking-wide">Comprendre</span>
              </div>
              {section.title && <h2 className="text-2xl font-bold text-gray-900 mb-5">{section.title}</h2>}
              {section.content && <p className="text-gray-600 leading-relaxed mb-6">{section.content}</p>}
              {section.items && (
                <div className="space-y-3">
                  {section.items.map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <span className="w-6 h-6 bg-blue-600 text-white rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                      <p className="text-gray-700 text-sm leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        );

      case 'example':
        return (
          <section key={idx} className="py-16 px-4 bg-slate-50">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-7 bg-green-500 rounded-full" />
                <span className="text-sm font-semibold text-green-600 uppercase tracking-wide">Exemple concret</span>
              </div>
              {section.title && <h2 className="text-xl font-bold text-gray-900 mb-4">{section.title}</h2>}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                {section.content && <p className="text-gray-700 leading-relaxed">{section.content}</p>}
                {section.items && (
                  <ul className="mt-4 space-y-3">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                        <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </section>
        );

      case 'solution':
        return (
          <section key={idx} className="py-16 px-4 bg-white">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-7 bg-blue-600 rounded-full" />
                <span className="text-sm font-semibold text-blue-600 uppercase tracking-wide">La solution</span>
              </div>
              {section.title && <h2 className="text-2xl font-bold text-gray-900 mb-5">{section.title}</h2>}
              {section.content && <p className="text-gray-600 leading-relaxed mb-6">{section.content}</p>}
              {section.items && (
                <div className="grid sm:grid-cols-2 gap-4">
                  {section.items.map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100">
                      <CheckCircle size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-gray-700 leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => nav('/beta-founders')}
                  className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-7 py-3.5 rounded-xl hover:bg-blue-700 font-bold shadow transition-all"
                >
                  Tester Trezoone <ArrowRight size={18} />
                </button>
                <button
                  onClick={() => nav('/simulateur-tresorerie-association')}
                  className="inline-flex items-center justify-center gap-2 border border-gray-200 text-gray-700 px-7 py-3.5 rounded-xl hover:border-blue-300 hover:text-blue-600 transition-all font-medium"
                >
                  Simuler ma trésorerie
                </button>
              </div>
            </div>
          </section>
        );

      case 'checklist':
        return (
          <section key={idx} className="py-14 px-4 bg-gray-50">
            <div className="max-w-3xl mx-auto">
              {section.title && <h2 className="text-xl font-bold text-gray-900 mb-6">{section.title}</h2>}
              <div className="space-y-3">
                {section.items?.map((item, i) => (
                  <label key={i} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200 cursor-default">
                    <CheckCircle size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </label>
                ))}
              </div>
            </div>
          </section>
        );

      case 'faq':
        return (
          <section key={idx} className="py-16 px-4 bg-white">
            <div className="max-w-2xl mx-auto">
              {section.title && <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">{section.title}</h2>}
              <div className="space-y-4">
                {section.faq?.map((item, i) => (
                  <details key={i} className="border border-gray-200 rounded-xl overflow-hidden group">
                    <summary className="flex items-center justify-between px-6 py-4 cursor-pointer bg-white hover:bg-gray-50 transition-colors list-none">
                      <span className="font-semibold text-gray-900 text-sm pr-4">{item.q}</span>
                      <ChevronRight size={16} className="text-gray-400 flex-shrink-0 group-open:rotate-90 transition-transform" />
                    </summary>
                    <div className="px-6 pb-5 pt-2 bg-white border-t border-gray-100">
                      <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        );

      case 'links':
        return (
          <section key={idx} className="py-12 px-4 bg-gray-50 border-t border-gray-100">
            <div className="max-w-4xl mx-auto">
              {section.title && <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">{section.title}</h3>}
              <div className="flex flex-wrap gap-2">
                {section.links?.map((link, i) => (
                  link.internal !== false ? (
                    <Link
                      key={i}
                      to={link.href}
                      className="flex items-center gap-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg px-3 py-2 hover:border-blue-300 hover:text-blue-600 transition-all bg-white"
                    >
                      <BookOpen size={12} />
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      key={i}
                      href={link.href}
                      className="flex items-center gap-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg px-3 py-2 hover:border-blue-300 hover:text-blue-600 transition-all bg-white"
                    >
                      <BookOpen size={12} />
                      {link.label}
                    </a>
                  )
                ))}
              </div>
            </div>
          </section>
        );

      case 'cta':
        return (
          <section key={idx} className="py-20 px-4 bg-gradient-to-br from-gray-900 to-gray-800">
            <div className="max-w-xl mx-auto text-center text-white">
              {section.title && <h2 className="text-2xl sm:text-3xl font-bold mb-4">{section.title}</h2>}
              {section.content && <p className="text-gray-400 mb-8 text-sm leading-relaxed">{section.content}</p>}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => nav('/beta-founders')}
                  className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-7 py-3.5 rounded-xl hover:bg-blue-700 font-bold shadow-lg transition-all"
                >
                  Tester Trezoone <ArrowRight size={18} />
                </button>
                <button
                  onClick={() => nav('/simulateur-tresorerie-association')}
                  className="inline-flex items-center justify-center gap-2 border border-gray-600 text-gray-300 px-7 py-3.5 rounded-xl hover:border-gray-400 hover:text-white transition-all font-medium"
                >
                  Simuler ma trésorerie
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-4">Sans carte bancaire · Sans engagement · 14 jours offerts</p>
            </div>
          </section>
        );

      case 'midcta':
        return (
          <section key={idx} className="py-12 px-4 bg-blue-600">
            <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="text-white text-center sm:text-left">
                {section.title && <p className="font-bold text-lg">{section.title}</p>}
                {section.content && <p className="text-blue-100 text-sm mt-1">{section.content}</p>}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                <button
                  onClick={() => nav('/beta-founders')}
                  className="inline-flex items-center justify-center gap-2 bg-white text-blue-700 px-6 py-3 rounded-xl hover:bg-blue-50 font-bold shadow transition-all text-sm"
                >
                  Tester Trezoone <ArrowRight size={16} />
                </button>
                <button
                  onClick={() => nav('/simulateur-tresorerie-association')}
                  className="inline-flex items-center justify-center gap-2 border border-blue-400 text-white px-6 py-3 rounded-xl hover:border-white transition-all font-medium text-sm"
                >
                  Simuler ma trésorerie
                </button>
              </div>
            </div>
          </section>
        );

      case 'cluster':
        return (
          <section key={idx} className="py-14 px-4 bg-slate-50 border-y border-slate-100">
            <div className="max-w-4xl mx-auto">
              {section.title && <h2 className="text-lg font-bold text-gray-900 mb-6">{section.title}</h2>}
              {section.content && <p className="text-gray-600 text-sm mb-5">{section.content}</p>}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {section.links?.map((link, i) => (
                  link.internal !== false ? (
                    <Link
                      key={i}
                      to={link.href}
                      className="flex items-center gap-2 p-4 bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all group"
                    >
                      <ChevronRight size={14} className="text-blue-500 group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
                      <span className="text-sm text-gray-700 group-hover:text-blue-600 font-medium">{link.label}</span>
                    </Link>
                  ) : (
                    <a
                      key={i}
                      href={link.href}
                      className="flex items-center gap-2 p-4 bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all group"
                    >
                      <ChevronRight size={14} className="text-blue-500 group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
                      <span className="text-sm text-gray-700 group-hover:text-blue-600 font-medium">{link.label}</span>
                    </a>
                  )
                ))}
              </div>
            </div>
          </section>
        );

      default:
        return null;
    }
  }

  return (
    <>
      <SeoHead
        title={data.seo.title}
        description={data.seo.description}
        canonical={data.seo.canonical}
        keywords={data.seo.keywords}
      />
      <div className="min-h-screen bg-white">
        <MarketingHeader onNavigate={nav} onSignIn={onSignIn ?? (() => navigate('/login'))} onSignUp={signUp} />

        {/* HERO */}
        <section className="bg-gradient-to-b from-slate-50 to-white pt-12 pb-20 px-4">
          <div className="max-w-4xl mx-auto text-center">
            {data.hero.tag && (
              <div className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold mb-6">
                <TrendingUp size={12} />
                {data.hero.tag}
              </div>
            )}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight mb-6 tracking-tight">
              {data.hero.title}
            </h1>
            <p className="text-lg text-gray-500 mb-8 leading-relaxed max-w-2xl mx-auto">
              {data.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => nav(data.hero.ctaHref ?? '/beta-founders')}
                className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-xl hover:bg-blue-700 font-bold shadow-lg transition-all text-base"
              >
                {data.hero.ctaLabel ?? 'Tester Trezoone gratuitement'} <ArrowRight size={18} />
              </button>
              <button
                onClick={() => nav('/simulateur-tresorerie-association')}
                className="inline-flex items-center justify-center gap-2 border border-gray-200 text-gray-700 px-8 py-4 rounded-xl hover:border-blue-300 hover:text-blue-600 transition-all font-medium"
              >
                Simuler ma trésorerie
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-3">Sans carte bancaire · Essai gratuit 14 jours</p>
          </div>
        </section>

        {/* MAILLAGE PILIER */}
        {data.pilierLink && (
          <div className="bg-blue-50 border-y border-blue-100 py-3 px-4">
            <div className="max-w-4xl mx-auto flex items-center gap-2 text-sm text-blue-700">
              <BookOpen size={14} />
              <span>Guide complet :</span>
              <Link to={data.pilierLink.href} className="font-semibold hover:underline">
                {data.pilierLink.label} →
              </Link>
            </div>
          </div>
        )}

        {/* SECTIONS DYNAMIQUES */}
        {data.sections.map((s, i) => renderSection(s, i))}

        {/* CONTENU ENFANT OPTIONNEL */}
        {children}

        {/* MAILLAGE INTERNE */}
        {data.relatedLinks && data.relatedLinks.length > 0 && (
          <section className="py-12 px-4 bg-gray-50 border-t border-gray-100">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Pages liées</h3>
              <div className="flex flex-wrap gap-2">
                {data.relatedLinks.map((link, i) => (
                  <Link
                    key={i}
                    to={link.href}
                    className="flex items-center gap-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg px-3 py-2 hover:border-blue-300 hover:text-blue-600 transition-all bg-white"
                  >
                    {link.label} <ArrowRight size={12} />
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <MarketingFooter />
      </div>
    </>
  );
}
